import { useCallback, useMemo, useState } from "react";
import { Form, useToast, type FormInstance } from "@ir/ui";
import {
  CreateMasterDataDistributionCenterDTO,
  UpdateMasterDataDistributionCenterDTO,
} from "@src/domain/dto/masterData";
import type { MasterDataDistributionCenter } from "@src/domain/entities/masterData";
import {
  CreateMasterDataDistributionCenterUseCase,
  GetMasterDataPurchasersUseCase,
  UpdateMasterDataDistributionCenterUseCase,
} from "@src/application/useCases/masterData";
import {
  MasterDataDistributionCenterRepository,
  MasterDataPurchaserRepository,
} from "@src/infrastructure/repositories/masterData";
import { loadMasterDataPurchasersLookup } from "@src/presentation/providers/masterData/loadMasterDataPurchasersLookup";
import { getMasterDataPurchasersLookupCache } from "@src/presentation/providers/masterData/masterDataListCache";
import { useMasterDataBrand } from "../MasterDataBrandContext";
import {
  DC_CODE_ALREADY_EXISTS_MESSAGE,
  DC_SAVE_NETWORK_ERROR_MESSAGE,
  isDcCodeDuplicateError,
  isDcSaveNetworkError,
} from "./dcCodeValidation";
import type {
  DistributionCenterFormMode,
  DistributionCenterFormValues,
  PurchaserSelectOption,
} from "./MasterDataDistributionCenterAddEditForm";

const EMPTY_FORM_VALUES: DistributionCenterFormValues = {
  purchaserId: null,
  dcName: "",
  dcCode: "",
};

interface UseMasterDataDistributionCenterFormOptions {
  onSaved: () => void;
  lockedPurchaserId?: number | null;
  lockedPurchaserName?: string;
}

export function useMasterDataDistributionCenterForm({
  onSaved,
  lockedPurchaserId = null,
  lockedPurchaserName = "",
}: UseMasterDataDistributionCenterFormOptions) {
  const { brandKey } = useMasterDataBrand();
  const { success: successToast, error: errorToast } = useToast();
  const [form] = Form.useForm<DistributionCenterFormValues>();
  const [formMode, setFormMode] = useState<DistributionCenterFormMode | null>(
    null,
  );
  const [editingEntity, setEditingEntity] =
    useState<MasterDataDistributionCenter | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isFormSubmittable, setIsFormSubmittable] = useState(false);
  const [purchaserOptions, setPurchaserOptions] = useState<
    PurchaserSelectOption[]
  >([]);
  const [purchasersLoading, setPurchasersLoading] = useState(false);

  const repository = useMemo(
    () => new MasterDataDistributionCenterRepository(),
    [],
  );
  const createUseCase = useMemo(
    () => new CreateMasterDataDistributionCenterUseCase(repository),
    [repository],
  );
  const updateUseCase = useMemo(
    () => new UpdateMasterDataDistributionCenterUseCase(repository),
    [repository],
  );
  const getPurchasersUseCase = useMemo(
    () => new GetMasterDataPurchasersUseCase(new MasterDataPurchaserRepository()),
    [],
  );

  const isPurchaserLocked = lockedPurchaserId != null || formMode === "edit";

  const lockedName = useMemo(() => {
    if (formMode === "edit" && editingEntity) {
      return editingEntity.purchaserName ?? lockedPurchaserName;
    }
    return lockedPurchaserName;
  }, [editingEntity, formMode, lockedPurchaserName]);

  const initialValues = useMemo((): DistributionCenterFormValues => {
    if (formMode === "edit" && editingEntity) {
      return {
        purchaserId: editingEntity.purchaserId,
        dcName: editingEntity.dcName,
        dcCode: editingEntity.dcCode,
      };
    }
    return {
      ...EMPTY_FORM_VALUES,
      purchaserId: lockedPurchaserId,
    };
  }, [editingEntity, formMode, lockedPurchaserId]);

  const closeForm = useCallback(
    (formInstance?: FormInstance<DistributionCenterFormValues>) => {
      setFormMode(null);
      setEditingEntity(null);
      setIsFormSubmittable(false);
      (formInstance ?? form).resetFields();
    },
    [form],
  );

  const ensurePurchaserOptions = useCallback(async () => {
    if (lockedPurchaserId != null || !brandKey) {
      return;
    }
    const cached = getMasterDataPurchasersLookupCache<{
      purchaserId: number;
      purchaserName: string;
    }>(brandKey);
    if (cached) {
      setPurchaserOptions(cached);
      return;
    }
    setPurchasersLoading(true);
    try {
      const options = await loadMasterDataPurchasersLookup(
        brandKey,
        getPurchasersUseCase,
      );
      setPurchaserOptions(options);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load purchasers";
      errorToast({ content: message });
      setPurchaserOptions([]);
    } finally {
      setPurchasersLoading(false);
    }
  }, [brandKey, errorToast, getPurchasersUseCase, lockedPurchaserId]);

  const openCreate = useCallback(() => {
    setEditingEntity(null);
    setFormMode("create");
    setIsFormSubmittable(false);
    void ensurePurchaserOptions();
  }, [ensurePurchaserOptions]);

  const openEdit = useCallback((entity: MasterDataDistributionCenter) => {
    setEditingEntity(entity);
    setFormMode("edit");
    setIsFormSubmittable(false);
  }, []);

  const handleSubmit = useCallback(
    async (values: DistributionCenterFormValues) => {
      setIsSaving(true);
      try {
        const dcName = values.dcName.trim();
        const dcCode = values.dcCode.trim();
        const purchaserId =
          lockedPurchaserId ??
          editingEntity?.purchaserId ??
          values.purchaserId;

        if (formMode === "edit" && editingEntity) {
          await updateUseCase.execute(
            new UpdateMasterDataDistributionCenterDTO({
              distributionCenterId: editingEntity.distributionCenterId,
              dcName,
              dcCode,
            }),
          );
        } else {
          if (purchaserId == null || purchaserId <= 0) {
            form.setFields([
              {
                name: "purchaserId",
                errors: ["Purchaser is required"],
              },
            ]);
            setIsFormSubmittable(false);
            return;
          }
          await createUseCase.execute(
            new CreateMasterDataDistributionCenterDTO({
              purchaserId,
              dcName,
              dcCode,
            }),
          );
        }

        successToast({
          content: "Distribution center saved successfully",
        });
        closeForm();
        onSaved();
      } catch (err) {
        if (isDcCodeDuplicateError(err)) {
          form.setFields([
            {
              name: "dcCode",
              errors: [DC_CODE_ALREADY_EXISTS_MESSAGE],
            },
          ]);
          setIsFormSubmittable(false);
        } else if (isDcSaveNetworkError(err)) {
          errorToast({ content: DC_SAVE_NETWORK_ERROR_MESSAGE });
        } else if (err instanceof Error && err.message.trim()) {
          errorToast({ content: err.message });
        } else {
          errorToast({
            content: "Failed to save distribution center. Please try again.",
          });
        }
      } finally {
        setIsSaving(false);
      }
    },
    [
      closeForm,
      createUseCase,
      editingEntity,
      errorToast,
      form,
      formMode,
      lockedPurchaserId,
      onSaved,
      successToast,
      updateUseCase,
    ],
  );

  return {
    form,
    formMode,
    isSaving,
    isFormSubmittable,
    setIsFormSubmittable,
    initialValues,
    purchaserOptions,
    purchasersLoading,
    isPurchaserLocked,
    lockedPurchaserName: lockedName,
    openCreate,
    openEdit,
    closeForm,
    handleSubmit,
  };
}
